/**
 * FAQのURLを生成する
 *
 * siteUrl、pathPrefix、pathnameを組み合わせてFAQのURLを構築します。
 * 各パラメータは防御的に正規化され、スラッシュの重複を防ぎます。
 *
 * @param siteUrl - サイトのベースURL（例: "https://example.com"）末尾スラッシュなし推奨
 * @param pathPrefix - パスプレフィックス（例: "faq", "test"）前後スラッシュなし推奨
 * @param pathname - ページパス（例: "/answer/123"）
 * @returns 構築されたURL、siteUrlがない場合は相対パス
 *
 * @example
 * // 基本的な使用例
 * buildFaqUrl('https://example.com', null, '/answer/123')
 * // => 'https://example.com/answer/123'
 *
 * @example
 * // pathPrefixあり
 * buildFaqUrl('https://example.com', 'faq', '/answer/123')
 * // => 'https://example.com/faq/answer/123'
 *
 * @example
 * // siteUrlなしの場合は相対パス
 * buildFaqUrl(null, 'faq', '/answer/123')
 * // => '/faq/answer/123'
 *
 * @example
 * // 防御的処理: 不正なスラッシュも正規化（末尾スラッシュは保持）
 * buildFaqUrl('https://example.com/', '/faq/', '/answer/123/')
 * // => 'https://example.com/faq/answer/123/'
 */
export function buildFaqUrl(
  siteUrl: string | null | undefined,
  pathPrefix: string | null | undefined,
  pathname: string
): string {
  // 防御的に末尾スラッシュを削除（スキーマで禁止されているが念のため）
  const base = siteUrl ? siteUrl.replace(/\/$/, '') : ''

  // pathPrefixがある場合のみ追加（前後スラッシュを削除して正規化）
  const prefix = pathPrefix ? `/${pathPrefix.replace(/^\/|\/$/g, '')}` : ''

  // pathnameがある場合のみ追加（先頭スラッシュのみ削除、末尾は保持）
  const normalizedPathname = pathname ? `/${pathname.replace(/^\/+/, '')}` : ''

  return `${base}${prefix}${normalizedPathname}`
}

/**
 * FAQ回答ページのURLを生成する
 *
 * siteUrlとpathPrefixは前後スラッシュなしの前提だが、防御的に正規化する。
 * スキーマバリデーションで前後スラッシュは禁止されているが、
 * データベースに既存の不正データがある場合や、
 * APIからの直接更新時の安全性を考慮して正規化処理を実施する。
 *
 * @param siteUrl - サイトのベースURL（末尾スラッシュなし推奨、例: "https://example.com"）
 * @param pathPrefix - パスプレフィックス（前後スラッシュなし、例: "test", "faq"）
 * @param id - 回答ページのID（カードID）
 * @returns FAQ回答ページの完全なURL
 *
 * @example
 * // pathPrefixなし
 * buildFaqAnswerUrl('https://example.com', null, '123')
 * // => 'https://example.com/answer/123/'
 *
 * @example
 * // pathPrefixあり
 * buildFaqAnswerUrl('https://example.com', 'test', '123')
 * // => 'https://example.com/test/answer/123/'
 *
 * @example
 * // 防御的処理: 末尾スラッシュがある場合でも正しく処理
 * buildFaqAnswerUrl('https://example.com/', 'test', '123')
 * // => 'https://example.com/test/answer/123/'
 */
export function buildFaqAnswerUrl(
  siteUrl: string | null | undefined,
  pathPrefix: string | null | undefined,
  id: string
): string {
  return buildFaqUrl(siteUrl, pathPrefix, `/answer/${id}/`)
}

/**
 * サブカテゴリのパスを解決する
 *
 * インデックスページ（"/"）からの遷移の場合は /search を挿入します。
 * それ以外の場合は現在のパスにIDを追加します。
 *
 * @param path - 現在のパス（例: "/"、"/search"、"/category/1"）
 * @param id - サブカテゴリのID
 * @returns 解決されたサブカテゴリパス
 *
 * @example
 * // インデックスページから
 * resolveSubCategoryPath('/', 'category1')
 * // => '/search/category1'
 *
 * @example
 * // 既存の検索パスから
 * resolveSubCategoryPath('/search', 'category1')
 * // => '/search/category1'
 */
export function resolveSubCategoryPath(path: string, id: string | undefined) {
  return removeDuplicate(`${path === '/' ? '/search' : path}/${id}`)
}

/**
 * 親パスを解決する（最後のセグメントを削除）
 *
 * パスから最後のセグメントを削除して親パスを取得します。
 *
 * @param path - 現在のパス（例: "/search/category1/subcategory"）
 * @returns 親パス（例: "/search/category1"）
 *
 * @example
 * resolveParentCategoryPath('/search/category1/subcategory')
 * // => '/search/category1'
 *
 * @example
 * resolveParentCategoryPath('/search/category1/')
 * // => '/search'
 */
export function resolveParentCategoryPath(path: string) {
  return path.replace(/\/[^/]+\/?$/g, '')
}

/**
 * 重複するスラッシュを削除する
 *
 * パス内の連続したスラッシュ（//）を単一のスラッシュ（/）に置換します。
 *
 * @param target - 対象の文字列
 * @returns スラッシュが正規化された文字列
 *
 * @example
 * removeDuplicate('/search//category1')
 * // => '/search/category1'
 *
 * @example
 * removeDuplicate('///multiple///slashes///')
 * // => '/multiple/slashes/'
 */
export function removeDuplicate(target: string) {
  return target.replace(/\/\//g, '/')
}

/**
 * FAQ URLからanswerIdを抽出する
 *
 * URLパス内の /answer/ セグメントの次のセグメントをanswerIdとして抽出します。
 * クエリパラメータやハッシュは自動的に除外されます。
 *
 * @param url - FAQ answer URL（完全なURLまたはパスのみ）
 * @returns answerId（抽出できない場合はnull）
 *
 * @example
 * // 基本的な使用例
 * extractAnswerIdFromUrl('https://example.com/answer/123/')
 * // => '123'
 *
 * @example
 * // pathPrefix付き
 * extractAnswerIdFromUrl('https://example.com/test/answer/123/')
 * // => '123'
 *
 * @example
 * // クエリパラメータ付き
 * extractAnswerIdFromUrl('https://example.com/answer/123/?suggest=true')
 * // => '123'
 *
 * @example
 * // パスのみ
 * extractAnswerIdFromUrl('/answer/123/')
 * // => '123'
 *
 * @example
 * // answerセグメントがない場合
 * extractAnswerIdFromUrl('https://example.com/search')
 * // => null
 */
export function extractAnswerIdFromUrl(url: string): string | null {
  try {
    // URLオブジェクトを使用してパスを解析
    // 相対URLの場合はダミーのベースURLを使用
    const urlObj = url.startsWith('/') ? new URL(url, 'http://dummy.local') : new URL(url)

    // パスをセグメントに分割（空文字列を除外）
    const pathSegments = urlObj.pathname.split('/').filter(segment => segment !== '')

    // /answer/ セグメントのインデックスを探す
    const answerIndex = pathSegments.indexOf('answer')

    // answerセグメントが見つからない、または次のセグメントがない場合
    if (answerIndex === -1 || answerIndex >= pathSegments.length - 1) {
      return null
    }

    // answerの次のセグメントを返す
    return pathSegments[answerIndex + 1]
  } catch (error) {
    // URL解析エラーの場合はnullを返す
    return null
  }
}

/**
 * FAQサイトのURLを生成する
 *
 * 防御的に各パーツを正規化し、ダブルスラッシュを防ぐ。
 *
 * @param siteUrl - サイトのベースURL（末尾スラッシュなし推奨）
 * @param pathPrefix - パスプレフィックス（前後スラッシュなし推奨）
 * @param botId - botID（siteUrlがない場合のフォールバック用）
 * @param pathname - ページパス（先頭スラッシュあり推奨）
 * @returns FAQサイトの完全なURL
 *
 * @example
 * // pathPrefixなし
 * toUrl('https://example.com', null, null, '/search')
 * // => 'https://example.com/search'
 *
 * @example
 * // pathPrefixあり
 * toUrl('https://example.com', 'test', null, '/search')
 * // => 'https://example.com/test/search'
 *
 * @example
 * // 防御的処理: 末尾・先頭スラッシュがある場合でも正しく処理
 * toUrl('https://example.com/', 'test', null, '/search')
 * // => 'https://example.com/test/search'
 */
export function toUrl(
  siteUrl: string | null | undefined,
  pathPrefix: string | null | undefined,
  botId?: string | null,
  pathname?: string
): string {
  // ベースURLの決定と正規化（末尾スラッシュを削除）
  const baseUrl = (siteUrl ?? (botId ? `https://faq.${botId}.karakuri.ai` : '')).replace(/\/$/, '')

  // pathPrefixの正規化（前後スラッシュを削除）
  const normalizedPrefix = pathPrefix?.replace(/^\/|\/$/g, '') || ''

  // pathnameの正規化（先頭スラッシュを確保）
  const normalizedPathname = pathname ? (pathname.startsWith('/') ? pathname : `/${pathname}`) : ''

  // URL組み立て
  if (normalizedPrefix) {
    return `${baseUrl}/${normalizedPrefix}${normalizedPathname}`
  }
  return `${baseUrl}${normalizedPathname}`
}
